import { defaultCurrency } from 'common/constants';
import {
  BudgetMonthBase,
  BudgetValueState,
  IBudget,
  Income
} from 'common/entities';
import { Currency, IMonth } from 'common/types';
import { useAppDispatch, useAppSelector } from 'core/hooks/hooks';
import { setBudgetMonth } from 'core/store/actions/budgetMonth.actions';
import { budgetsSelector } from 'core/store/selectors/budgets.selector';
import { incomesSelector } from 'core/store/selectors/incomes.selector';
import { budgetMonthSelector } from 'core/store/selectors/monthBudget.selector';
import { periodSelector } from 'core/store/selectors/period.selector';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState
} from 'react';

export type BudgetMonthContextType = {
  isEditing: boolean;
  budgetMonthCreated: boolean;
  setIsEditing: (isEditing: boolean) => void;
  selectedBudgets: Record<string, BudgetValueState>;
  selectedIncomes: Record<string, BudgetValueState>;
  onUpdateBudgetValue: (budgetId: string, value: number | undefined) => void;
  onUpdateIncomeValue: (incomeId: string, value: number | undefined) => void;
  onBudgetToggleSelected: (budgetId: string) => void;
  onIncomeToggleSelected: (incomeId: string) => void;
  budgetValues: Record<string, BudgetValueState>;
  incomesValues: Record<string, BudgetValueState>;
  prevPeriodResidueState?: number;
  onPrevPeriodResidueChange: (value: number | undefined) => void;
  onSaveBudgetMonth: () => void;
  selectedCurrency: Currency;
  savedCurrency: Currency;
  onUpdateCurrency: (currency: Currency) => void;
};

const defaultContextValue: BudgetMonthContextType = {
  isEditing: false,
  budgetMonthCreated: false,
  selectedBudgets: {},
  selectedIncomes: {},
  setIsEditing: () => {},
  onUpdateBudgetValue: () => {},
  onUpdateIncomeValue: () => {},
  onBudgetToggleSelected: () => {},
  onIncomeToggleSelected: () => {},
  budgetValues: {},
  incomesValues: {},
  onSaveBudgetMonth: () => {},
  prevPeriodResidueState: undefined,
  onPrevPeriodResidueChange: () => {},
  selectedCurrency: defaultCurrency,
  savedCurrency: defaultCurrency,
  onUpdateCurrency: () => {}
};

export const BudgetMonthContext =
  createContext<BudgetMonthContextType>(defaultContextValue);

const filterSelected = (
  items: Record<string, BudgetValueState>
): Record<string, BudgetValueState> =>
  Object.entries(items).reduce((acc, [key, value]) => {
    if (value.selected) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, BudgetValueState>);

const createBudgetMonth = (
  yearMonth: IMonth,
  budgetsState: Record<string, BudgetValueState>,
  incomesState: Record<string, BudgetValueState>,
  prevPeriodResidue: number | undefined,
  currency?: Currency,
  userCurrency?: Currency
): BudgetMonthBase => {
  return {
    year: yearMonth.year,
    month: yearMonth.month,
    selectedBudgets: filterSelected(budgetsState),
    selectedIncomes: filterSelected(incomesState),
    prevPeriodResidue,
    currency: currency || userCurrency || defaultCurrency
  };
};

const createBudgetsEditingState = (
  budgets: IBudget[],
  selectedBudgets: Record<string, BudgetValueState>
) => {
  return budgets
    .filter(budget => !budget.closed)
    .reduce((acc, budget) => {
      acc[budget.id] = {
        value: selectedBudgets[budget.id]?.value ?? budget.defaultBudget,
        selected:
          selectedBudgets[budget.id]?.selected ?? !!budget.defaultSelected
      };
      return acc;
    }, {} as Record<string, BudgetValueState>);
};

const createIncomesEditingState = (
  incomes: Income[],
  selectedIncomes: Record<string, BudgetValueState>
) => {
  return incomes.reduce((acc, income) => {
    acc[income.id] = {
      value: selectedIncomes[income.id]?.value,
      selected: selectedIncomes[income.id]?.selected ?? false
    };
    return acc;
  }, {} as Record<string, BudgetValueState>);
};

const BudgetMonthContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const budgetMonth = useAppSelector(periodSelector);
  const { budgets } = useAppSelector(budgetsSelector);
  const { incomes } = useAppSelector(incomesSelector);
  const {
    prevPeriodResidue,
    budgetMonthCreated,
    selectedBudgets,
    selectedIncomes,
    currency
  } = useAppSelector(budgetMonthSelector(budgetMonth));

  const [budgetValuesInitialized, setBudgetValuesInitialized] = useState(false);
  const budgetsDefaultEditingState = createBudgetsEditingState(
    budgets,
    selectedBudgets ?? {}
  );
  const incomesDefaultEditingState = createIncomesEditingState(
    incomes,
    selectedIncomes ?? {}
  );

  const [prevPeriodResidueState, setPrevPeriodResidueState] = useState<
    number | undefined
  >(prevPeriodResidue);
  const [budgetValues, setBudgetValues] = useState<
    Record<string, BudgetValueState>
  >(budgetsDefaultEditingState);
  const [incomesValues, setIncomesValues] = useState<
    Record<string, BudgetValueState>
  >(incomesDefaultEditingState);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(
    currency || defaultCurrency
  );
  // const { currency: userCurrency } = useAppSelector(preferencesSelector);

  useEffect(() => {
    if (isEditing && !budgetValuesInitialized) {
      setBudgetValues(budgetsDefaultEditingState);
      setIncomesValues(incomesDefaultEditingState);
      setPrevPeriodResidueState(prevPeriodResidue);
      setBudgetValuesInitialized(true);
    }
  }, [isEditing]);

  useEffect(() => {
    if (isEditing) {
      setIsEditing(false);
    }
    setBudgetValues(createBudgetsEditingState(budgets, selectedBudgets || {}));
    setIncomesValues(createIncomesEditingState(incomes, selectedIncomes || {}));
    setPrevPeriodResidueState(prevPeriodResidue);
  }, [budgetMonth]);

  const handleSetEditing = (editing: boolean) => {
    setIsEditing(editing);
  };

  const handleBudgetValueChange = (
    budgetId: string,
    value: number | undefined
  ) => {
    setBudgetValues(prev => ({
      ...prev,
      [budgetId]: {
        ...prev[budgetId],
        value
      }
    }));
  };

  const handleIncomeValueChange = (
    incomeId: string,
    value: number | undefined
  ) => {
    setIncomesValues(prev => ({
      ...prev,
      [incomeId]: {
        ...prev[incomeId],
        value
      }
    }));
  };

  const handlePrevPeriodResidueChange = (value: number | undefined) => {
    setPrevPeriodResidueState(value);
  };

  const handleBudgetToggleSelected = (budgetId: string) => {
    setBudgetValues(prev => ({
      ...prev,
      [budgetId]: {
        ...prev[budgetId],
        selected: !prev[budgetId]?.selected
      }
    }));
  };

  const handleIncomeToggleSelected = (incomeId: string) => {
    setIncomesValues(prev => ({
      ...prev,
      [incomeId]: {
        ...prev[incomeId],
        selected: !prev[incomeId]?.selected
      }
    }));
  };

  const handleSaveBudgetMonth = useCallback(() => {
    const budgetMonthToSave = createBudgetMonth(
      budgetMonth,
      budgetValues,
      incomesValues,
      prevPeriodResidueState,
      selectedCurrency
    );

    dispatch(setBudgetMonth(budgetMonthToSave));
    setIsEditing(false);
  }, [
    budgetMonth,
    budgetValues,
    incomesValues,
    prevPeriodResidueState,
    selectedCurrency
  ]);

  return (
    <BudgetMonthContext.Provider
      value={{
        isEditing,
        budgetMonthCreated,
        setIsEditing: handleSetEditing,
        selectedBudgets: selectedBudgets || {},
        selectedIncomes: selectedIncomes || {},
        onUpdateBudgetValue: handleBudgetValueChange,
        onUpdateIncomeValue: handleIncomeValueChange,
        onBudgetToggleSelected: handleBudgetToggleSelected,
        onIncomeToggleSelected: handleIncomeToggleSelected,
        budgetValues,
        incomesValues,
        prevPeriodResidueState,
        onPrevPeriodResidueChange: handlePrevPeriodResidueChange,
        onSaveBudgetMonth: handleSaveBudgetMonth,
        selectedCurrency,
        savedCurrency: currency || defaultCurrency,
        onUpdateCurrency: setSelectedCurrency
      }}
    >
      {children}
    </BudgetMonthContext.Provider>
  );
};

export default BudgetMonthContextProvider;
